import { ChevronLeft, ChevronRight, CircleQuestionMark, Settings } from 'lucide-react'
import * as React from 'react'
import { SidebarButton } from '@/components'
import { FeedbackTrigger } from '@/components/common/FeedbackTrigger'
import { SidebarProjectsDropdown } from '@/components/sidebar/SidebarProjectsDropdown'
import { useSidebarState } from '@/hooks/useSidebarState'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'

export interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
	currentPage?: string
}

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(({ currentPage, ...props }, ref) => {
	const [isCollapsed, setIsCollapsed] = useSidebarState(false)
	const { t } = useTranslation()

	const toggleCollapse = () => {
		setIsCollapsed(!isCollapsed)
	}

	return (
		<nav
			ref={ref}
			className={cn(
				'hidden relative z-10 shrink-0 h-full flex-col md:flex justify-between border-r border-gray-200 bg-card text-card-foreground ring-foreground/10 ring-1 font-medium transition-all duration-300 overflow-hidden',
				isCollapsed ? 'w-[calc(5rem+2px)]' : 'w-[16rem]'
			)}
			{...props}
		>
			<div className="flex flex-col p-5 gap-3 min-w-0 overflow-y-auto">
				<SidebarProjectsDropdown currentPage={currentPage} collapsed={isCollapsed} />
			</div>

			<div className="flex flex-col p-5 gap-3 border-t border-gray-200">
				<SidebarButton
					label={t('navigation.settings')}
					icon={<Settings />}
					currentPage={currentPage}
					href="/settings"
					collapsed={isCollapsed}
				/>

				<SidebarButton
					label={t('navigation.help')}
					icon={<CircleQuestionMark />}
					currentPage={currentPage}
					href="/help"
					collapsed={isCollapsed}
				/>

				<FeedbackTrigger collapsed={isCollapsed} />
			</div>

			<button
				type="button"
				aria-label={t('sidebar.toggle')}
				onClick={toggleCollapse}
				className="absolute top-1/2 right-4 transform -translate-y-1/2 translate-x-1/2 bg-white hover:cursor-pointer active:scale-95 hover:scale-120 transition-all duration-300 ease-in-out"
			>
				{isCollapsed ? (
					<ChevronRight className="text-gray-700" />
				) : (
					<ChevronLeft className="text-gray-700" />
				)}
			</button>
		</nav>
	)
})
Sidebar.displayName = 'Sidebar'

export { Sidebar }
